// React hook for UI telemetry integration
// Provides easy access to UI metrics tracking in components

import { useEffect, useCallback } from 'react';
import { useFeatureFlag } from '../contexts/FeatureFlagProvider';
import uiMetrics from '../utils/uiMetrics';

export const useUIMetrics = () => {
  const [metricsEnabled] = useFeatureFlag('metrics.enabled');
  
  useEffect(() => {
    // Enable UI metrics if flag is set
    if (metricsEnabled) {
      uiMetrics.enable();
    } else {
      uiMetrics.disable();
    }
  }, [metricsEnabled]);

  // Track compact mode toggle/usage
  const trackCompactMode = useCallback((component, enabled, action = 'toggle') => {
    if (!metricsEnabled) return;
    uiMetrics.trackCompactModeUsage(component, enabled, action);
  }, [metricsEnabled]);

  // Track navigation interactions
  const trackNavigation = useCallback((version, action, item = null) => {
    if (!metricsEnabled) return;
    uiMetrics.trackNavigationUsage(version, action, item);
  }, [metricsEnabled]);

  // Track voice composer interactions when voice is disabled
  const trackVoiceComposer = useCallback((action, alternative = null) => {
    if (!metricsEnabled) return;
    uiMetrics.trackVoiceComposerUsage(action, alternative);
  }, [metricsEnabled]);

  // Track keyboard navigation success/failure
  const trackKeyboard = useCallback((component, key, success = true) => {
    if (!metricsEnabled) return;
    uiMetrics.trackKeyboardNavigation(component, key, success);
  }, [metricsEnabled]);

  // Track context menu usage
  const trackContextMenu = useCallback((component, action, method = 'mouse') => {
    if (!metricsEnabled) return;
    uiMetrics.trackContextMenuUsage(component, action, method);
  }, [metricsEnabled]);

  // Track view rendering performance
  const trackViewPerformance = useCallback((component, mode, itemCount) => {
    if (!metricsEnabled) return;
    
    const startTime = performance.now();
    
    // Return a function to call when rendering completes
    return () => {
      const renderTime = performance.now() - startTime;
      uiMetrics.trackViewPerformance(component, mode, renderTime, itemCount);
    };
  }, [metricsEnabled]);

  // Track viewport density (how many items fit above fold)
  const trackViewportDensity = useCallback((component, mode, visibleItems) => {
    if (!metricsEnabled) return;
    
    const viewportHeight = window.innerHeight;
    uiMetrics.trackViewportDensity(component, mode, visibleItems, viewportHeight);
  }, [metricsEnabled]);

  // Track search forwarding from header to command bar
  const trackSearchForwarding = useCallback((fromComponent, toComponent, query, resultCount) => {
    if (!metricsEnabled) return;
    uiMetrics.trackSearchForwarding(fromComponent, toComponent, query, resultCount);
  }, [metricsEnabled]);

  // Get current metrics summary
  const getMetricsSummary = useCallback(() => {
    return uiMetrics.getSummary();
  }, []);

  // Clear all metrics
  const clearMetrics = useCallback(() => {
    uiMetrics.clear();
  }, []);

  return {
    trackCompactMode,
    trackNavigation,
    trackVoiceComposer,
    trackKeyboard,
    trackContextMenu,
    trackViewPerformance,
    trackViewportDensity,
    trackSearchForwarding,
    getMetricsSummary,
    clearMetrics,
    isEnabled: metricsEnabled
  };
};